import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ProjectQueryParams,
  ProjectQueryParamsObject,
} from '@client/models/router.model';
import type { ProposalId } from '@shared/models/proposals.model';
import {
  PROPOSAL_SEARCH_PARAM,
  isBaselineProposalSearchValue,
  proposalIdFromSearchValue,
  useSelectedProposalId,
  useSetSelectedProposalId,
} from './use-selected-proposal-id.hooks';
import { useObjectMemoDeep } from './use-object.hooks';
import type { ProjectParamsObject } from './use-router.hooks';
import { createLocalStorageRecordStore } from '../helpers/local-storage.helpers';
import { useProjectStateStore } from '../store/project/project.store';
import {
  getBuildingVersionById,
  getAllBuildingVersions,
} from '@shared/helpers/recursive-element.helpers';

type RememberedProposalSelection = ProposalId | null;

const proposalSelectionByVersionStore = createLocalStorageRecordStore<
  string,
  RememberedProposalSelection
>('proposal_selection_by_version');

/**
 * Reads {@link ProjectQueryParams} from the current URL search string.
 * The returned object always contains every key defined in
 * {@link ProjectQueryParams}; values are `undefined` when the param is absent.
 *
 * Wrap reads here (instead of calling `useSearchParams` ad-hoc per consumer)
 * so URL-backed UI state stays a single, validated source of truth.
 */
export const useProjectQueryParams = (
  /** Route type the query params belong to. */
  type: ProjectQueryParamsObject['type'],
): ProjectQueryParamsObject => {
  const [searchParams] = useSearchParams();

  const data = useMemo<ProjectQueryParams>(() => {
    const proposal = searchParams.get(PROPOSAL_SEARCH_PARAM) ?? undefined;
    return { proposal };
  }, [searchParams]);

  return useObjectMemoDeep({ type, data });
};

/**
 * Returns a sync method that resets URL-backed UI state owned by query params
 * when the routing scope (project / version) changes.
 *
 * Currently clears a `?proposal=` carried into another project or version, and
 * leaves a different proposal on the destination URL alone. Add more reset
 * logic here as new query keys are introduced in {@link ProjectQueryParams}.
 */
export const useSyncQueryParamsMethod = () => {
  const selectedProposalId = useSelectedProposalId();
  const setSelectedProposalId = useSetSelectedProposalId();
  const [searchParams] = useSearchParams();
  const versionProposalStructure = useProjectStateStore((state) =>
    getAllBuildingVersions(state.project)
      .map(
        (version) =>
          `${version.id}:${(version.proposals ?? []).map((proposal) => proposal.id).join(',')}`,
      )
      .join('|'),
  );
  const explicitProposalId = proposalIdFromSearchValue(
    searchParams.get(PROPOSAL_SEARCH_PARAM),
  );
  const explicitBaselineSelected = isBaselineProposalSearchValue(
    searchParams.get(PROPOSAL_SEARCH_PARAM),
  );

  const previousScopeRef = useRef<RoutingScope | undefined>(undefined);
  const previousProposalIdRef = useRef<ProposalId | undefined>(undefined);

  return useCallback(
    (params: ProjectParamsObject) => {
      // Refresh when version or proposal ids change, not on every property edit.
      void versionProposalStructure;
      const scope = getRoutingScope(params);
      const previous = previousScopeRef.current;
      const scopeChanged = isScopeSwitch(previous, scope);
      const destinationVersion = isResolvedScope(scope)
        ? getBuildingVersionById(
            useProjectStateStore.getState().project,
            scope.versionId,
            false,
          )
        : undefined;

      // The route can switch before its destination version is present in the
      // store (notably after an optimistic version delete). Do not interpret a
      // proposal id from the previous/deleted version during that loading pass:
      // doing so would rewrite it to baseline before the destination's first
      // proposal can be resolved.
      if (shouldDeferProposalSync(scope, destinationVersion !== undefined)) {
        return;
      }

      const defaultProposalId = destinationVersion?.proposals?.[0]?.id;
      const rememberedSelection =
        scopeChanged && isResolvedScope(scope) && destinationVersion
          ? getRememberedProposalForVersion(
              scope.projectId,
              scope.versionId,
              defaultProposalId,
              destinationVersion.proposals.map(({ id }) => id),
            )
          : undefined;
      const validExplicitProposalId = destinationVersion?.proposals?.some(
        ({ id }) => id === explicitProposalId,
      )
        ? explicitProposalId
        : undefined;
      const nextProposalId = resolveProposalSelectionOnScopeSwitch({
        previousScope: previous,
        nextScope: scope,
        previousProposalId: previousProposalIdRef.current,
        nextProposalId: selectedProposalId,
        rememberedSelection,
        defaultProposalId,
        explicitNextProposalId: validExplicitProposalId,
        explicitBaselineSelected,
      });
      // List / loading scopes are not recorded, so A → list → B still compares A to B
      previousScopeRef.current = rememberRoutingScope(previous, scope);
      if (isResolvedScope(scope)) {
        previousProposalIdRef.current = nextProposalId;
        // Persist at selection time (including baseline), not when leaving the version.
        rememberProposalSelection(scope, nextProposalId);
      }

      const hasStaleExplicitProposal =
        explicitProposalId !== undefined &&
        destinationVersion !== undefined &&
        validExplicitProposalId === undefined;
      if (nextProposalId !== selectedProposalId || hasStaleExplicitProposal) {
        setSelectedProposalId(nextProposalId);
      }
    },
    [
      explicitBaselineSelected,
      explicitProposalId,
      selectedProposalId,
      setSelectedProposalId,
      versionProposalStructure,
    ],
  );
};

export type RoutingScope = {
  projectId: string | undefined;
  versionId: string | undefined;
};

/** A scope the URL has fully resolved, as opposed to one still being validated. */
const isResolvedScope = (
  scope: RoutingScope | undefined,
): scope is RoutingScope & { projectId: string; versionId: string } =>
  Boolean(scope?.projectId && scope.versionId);

/** Wait until a resolved route's version is available before resolving its proposal. */
export const shouldDeferProposalSync = (
  scope: RoutingScope,
  hasDestinationVersion: boolean,
): boolean => isResolvedScope(scope) && !hasDestinationVersion;

/**
 * The scope to remember after a URL update. Unresolved scopes (project list,
 * loading) are skipped so the last real project/version is still there to
 * compare when the next project opens.
 *
 * @param previous - Scope remembered from the last resolved URL
 * @param next - Scope of the URL that just rendered
 */
export const rememberRoutingScope = (
  previous: RoutingScope | undefined,
  next: RoutingScope,
): RoutingScope | undefined => {
  if (!isResolvedScope(next)) return previous;
  return next;
};

/**
 * Whether the URL moved from one project or version to another, which is the only
 * case that invalidates a proposal selection.
 *
 * Only a move between two **resolved** scopes counts. Opening a project link
 * directly renders once before the project is known, and treating that first
 * unresolved scope as a switch would drop a `?proposal=` that arrived with the
 * URL — the proposal of a shared or bookmarked link.
 */
export const isScopeSwitch = (
  previous: RoutingScope | undefined,
  next: RoutingScope,
): boolean => {
  if (!isResolvedScope(previous) || !isResolvedScope(next)) return false;

  return (
    previous?.projectId !== next.projectId ||
    previous?.versionId !== next.versionId
  );
};

type ProposalScopeTransition = {
  previousScope: RoutingScope | undefined;
  nextScope: RoutingScope;
  previousProposalId: ProposalId | undefined;
  nextProposalId: ProposalId | undefined;
};

type RememberedProposalScopeTransition = ProposalScopeTransition & {
  rememberedSelection: RememberedProposalSelection | undefined;
  defaultProposalId: ProposalId | undefined;
  explicitNextProposalId: ProposalId | undefined;
  explicitBaselineSelected: boolean;
};

const proposalSelectionScopeKey = (scope: RoutingScope): string | undefined =>
  isResolvedScope(scope) ? `${scope.projectId}:${scope.versionId}` : undefined;

const rememberProposalSelection = (
  scope: RoutingScope,
  proposalId: ProposalId | undefined,
): void => {
  const key = proposalSelectionScopeKey(scope);
  if (!key) return;
  proposalSelectionByVersionStore.setItem(key, proposalId ?? null);
};

const getRememberedProposalSelection = (
  scope: RoutingScope,
): RememberedProposalSelection | undefined => {
  const key = proposalSelectionScopeKey(scope);
  return key ? proposalSelectionByVersionStore.getItem(key) : undefined;
};

/** Stores the proposal to restore when a specific version is opened again. */
export const rememberProposalForVersion = (
  projectId: string | number,
  versionId: string,
  proposalId: ProposalId | undefined,
): void => {
  rememberProposalSelection(
    { projectId: String(projectId), versionId },
    proposalId,
  );
};

/** Baseline is transient when leaving a version; return its first proposal instead. */
export const getProposalToRememberAfterLeavingVersion = (
  selectedProposalId: ProposalId | undefined,
  firstProposalId: ProposalId | undefined,
): ProposalId | undefined => selectedProposalId ?? firstProposalId;

/**
 * Proposal selected for a specific local project/version. Baseline and unseen
 * versions fall back to the supplied first proposal so an opened version never
 * defaults to baseline.
 */
export const getRememberedProposalForVersion = (
  projectId: string | number,
  versionId: string,
  defaultProposalId?: ProposalId,
  validProposalIds?: readonly ProposalId[],
): ProposalId | undefined => {
  const remembered = getRememberedProposalSelection({
    projectId: String(projectId),
    versionId,
  });
  if (
    remembered &&
    (validProposalIds === undefined || validProposalIds.includes(remembered))
  ) {
    return remembered;
  }
  return defaultProposalId;
};

/**
 * Resolves the proposal for a destination version. A query value that differs
 * from the source is an explicit deep-link choice and wins. Otherwise the
 * locally remembered choice for that project/version is restored; baseline and
 * unseen destinations fall back to the first proposal. Within an already-open version,
 * clearing the query still selects baseline until the user leaves or reloads.
 */
export const resolveProposalSelectionOnScopeSwitch = ({
  previousScope,
  nextScope,
  previousProposalId,
  nextProposalId,
  rememberedSelection,
  defaultProposalId,
  explicitNextProposalId,
  explicitBaselineSelected,
}: RememberedProposalScopeTransition): ProposalId | undefined => {
  const scopeChanged = isScopeSwitch(previousScope, nextScope);
  const firstResolvedScope =
    !isResolvedScope(previousScope) && isResolvedScope(nextScope);
  if (!scopeChanged && !firstResolvedScope) return nextProposalId;
  if (explicitBaselineSelected) return undefined;
  if (explicitNextProposalId && explicitNextProposalId !== previousProposalId) {
    return explicitNextProposalId;
  }
  return rememberedSelection ?? defaultProposalId;
};

/**
 * Resets only a proposal carried into another scope. A different proposal in
 * the destination URL is intentional shareable-link state and must survive.
 */
export const shouldResetProposalOnScopeSwitch = ({
  previousScope,
  nextScope,
  previousProposalId,
  nextProposalId,
}: ProposalScopeTransition): boolean =>
  resolveProposalSelectionOnScopeSwitch({
    previousScope,
    nextScope,
    previousProposalId,
    nextProposalId,
    rememberedSelection: undefined,
    defaultProposalId: undefined,
    explicitNextProposalId: nextProposalId,
    explicitBaselineSelected: false,
  }) !== nextProposalId;

/**
 * Extracts the routing scope (projectId + versionId) from a validated
 * {@link ProjectParamsObject}. Used to detect when URL-backed UI state must be
 * reset.
 */
const getRoutingScope = (params: ProjectParamsObject): RoutingScope => {
  switch (params.type) {
    case 'project':
      return {
        projectId: params.data.projectId,
        versionId: params.data.versionId,
      };
    case 'shared':
      return {
        projectId: params.data.sharingKey,
        versionId: params.data.versionId,
      };
    case 'generation':
      return {
        projectId: params.data.generationKey,
        versionId: params.data.versionId,
      };
    case 'project-list':
    case 'error':
    default:
      return { projectId: undefined, versionId: undefined };
  }
};
